import { withTranslation } from 'react-i18next';

import Loading from '../../../../components/Loading';
import Metric from '../../../../components/Metric';
import PeriodTrend from '../../GscTools/components/PeriodTrend';
import { METRIC_COLORS, METRIC_FORMATS, deltaLabel } from '../../GscTools/format';
import { LOWER_IS_BETTER, comparability, metricDelta } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';

/**
 * Block 04 — the selected row's own daily history, in both periods.
 *
 * The two cards are the sums of the points actually drawn, not the row's figures from block 02:
 * a day Search Console did not return is absent from both the line and the sum, so the two agree
 * with each other and the coverage line says how many days that was. The sums are labelled as
 * matched-day sums for the same reason — the baseline card is the total of the days the baseline
 * window returned, aligned to the current window by day index.
 *
 * The chart itself is the shared period trend: one metric, one axis, the baseline dashed behind
 * the current line.
 */
function SelectedRowTrend({ t, scope, status, error, selectedKey, current, baseline, metric }) {
    const comparison = comparability(current?.coverage, baseline?.coverage ?? null);
    const metricLabel = t(`tools.gsc.metrics.${metric}`);

    // The delta sits on the current card, in the metric's own unit, and only where the two
    // windows returned every day they asked for: a sum over an incomplete window is not a figure
    // another sum can be subtracted from.
    const delta = comparison.comparable
        ? metricDelta(metric, current?.totals?.[metric], baseline?.totals?.[metric])
        : null;
    const changeLabel = delta ? deltaLabel(t, metric, delta) : null;
    const improved = LOWER_IS_BETTER[metric] ? delta?.absolute < 0 : delta?.absolute > 0;
    const worsened = LOWER_IS_BETTER[metric] ? delta?.absolute > 0 : delta?.absolute < 0;

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.pages_queries_changes.trend.title')}</p>
                    <p className="gsc-tool-sub">
                        {selectedKey
                            ? `${selectedKey} · ${formatPeriod(scope.current)}`
                            : t('tools.pages_queries_changes.trend.description')}
                    </p>
                </div>
            </div>

            <div className="card-body">
                {!selectedKey && (
                    <div className="gsc-tool-load-state">
                        <span className="gsc-tool-load-icon" aria-hidden="true">
                            <i className="icon-area-chart" />
                        </span>
                        <h4>{t('tools.pages_queries_changes.trend.idle_title')}</h4>
                        <p>{t('tools.pages_queries_changes.trend.idle')}</p>
                    </div>
                )}

                {!!selectedKey && status === 'loading' && <Loading padding={20} />}

                {!!selectedKey && status === 'error' && (
                    <div className="gsc-tool-banner error">
                        {error ?? t('global.something_went_wrong')}
                    </div>
                )}

                {!!selectedKey && status === 'done' && (
                    <>
                        <div className="metrics mb-2">
                            <Metric
                                metricType="primary"
                                color={METRIC_COLORS[metric]}
                                label={t('tools.pages_queries_changes.trend.current_card', {
                                    metric: metricLabel,
                                })}
                                value={current?.totals?.[metric] ?? null}
                                format={METRIC_FORMATS[metric]}
                                changeLabel={changeLabel}
                                changeDirection={improved ? 'up' : worsened ? 'down' : null}
                                note={t('tools.pages_queries_changes.trend.sum_note', {
                                    days: current?.coverage?.observed?.length ?? 0,
                                })}
                            />
                            <Metric
                                metricType="primary"
                                color={`${METRIC_COLORS[metric]}66`}
                                label={t('tools.pages_queries_changes.trend.prior_card', {
                                    metric: metricLabel,
                                })}
                                value={baseline?.totals?.[metric] ?? null}
                                format={METRIC_FORMATS[metric]}
                                changeLabel={null}
                                note={
                                    baseline
                                        ? t('tools.pages_queries_changes.trend.sum_note', {
                                              days: baseline?.coverage?.observed?.length ?? 0,
                                          })
                                        : t('tools.gsc.coverage.no_baseline')
                                }
                            />
                        </div>

                        <PeriodTrend
                            metric={metric}
                            current={current}
                            baseline={baseline}
                            fileName={t('tools.pages_queries_changes.trend.title')}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default withTranslation()(SelectedRowTrend);
